import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { ObjectUtils } from 'src/core/utils/object';
import { ContextService } from 'src/core/services/context.service';
import { ClientFormView } from './client-form.view';
import { ClientDataService } from './client-form.data';
import { EntitySchemaDataService } from 'src/app/general/entity-schemas/entity-schema.data';
import { IdentityService } from 'src/core/services/identity.service';
import { FormatService } from 'src/core/services/format.service';
import { QuoteListDataService } from 'src/app/bookings/quotes/list/quote-list.data';
import { ConfigurationService } from 'src/core/services/configuration.service';

interface Props extends ControllerProps<{ id: string }> {
}

interface State {
	mode: ControlMode;
	schema?: JsonSchema;
	defaultLanguage: string;
}

@observer
export class ClientFormController extends React.Component<Props, State>  {
	@resolve
	private contextService: ContextService;

	@resolve
	private formatService: FormatService;

	@resolve
	private identityService: IdentityService;

	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: ClientDataService;

	@observable
	private quoteListDataService: QuoteListDataService;
	@resolve
	configurationService: ConfigurationService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			mode: 'display',
			defaultLanguage: 'en'
		};
	}

	componentDidMount(): void {
		this.props.setBusy(true);
		this.configurationService.current()
			.then((appConfig) => {
				this.setState({ defaultLanguage: appConfig.defaultLanguage });
				Promise.all([this.loadData(), this.loadSchema()])
					.catch((error) => {
						this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
					})
					.then(() => {
						this.props.setBusy(false);
					});
			});
		
	}
	
	private async loadData(): Promise<void> {
		this.dataService = ClientDataService.make(this.contextService);
		this.quoteListDataService = QuoteListDataService.make(this.contextService, this.formatService);

		if (this.props.match.params.id == 'Person' || this.props.match.params.id == 'Company') {
			// Create new
			await this.dataService.add(this.props.match.params.id, this.identityService.userId,this.state.defaultLanguage);
			this.handleModeChange('edit');
		} else {
			// Load entity
			const clientId = parseInt(this.props.match.params.id);
			this.dataService.addFilter('id', 'eq', clientId);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;

			await this.dataService.load(query => query);
			await this.loadRelationship(this.quoteListDataService, clientId);
		}
	}

	private async loadRelationship(relationshipDataService, clientId: number, pageSize: number = 25, sortBy: string = 'id'): Promise<void> {
		relationshipDataService.addFilter('clientId', 'eq', clientId);
		relationshipDataService.addFilter('isDeleted', 'eq', false);
		relationshipDataService.addSorting(sortBy, 'asc', true);
		relationshipDataService.pageSize = pageSize;

		await relationshipDataService.load(query => query);
	}

	private async loadSchema(): Promise<void> {
		const entitySchemaService = EntitySchemaDataService.make();

		entitySchemaService.addFilter('name', 'eq', 'Client');
		entitySchemaService.addSorting('name', 'asc', true);
		entitySchemaService.pageSize = 1;

		const entitySchemas = await entitySchemaService.load(query => query);
		const schema = ObjectUtils.toObject<JsonSchema>(entitySchemas[0].schema);
		this.setState({ schema: schema });
	}

	@computed
	get title(): string {
		if (this.dataService && this.dataService.item) {
			if (this.dataService.item.type == 0) {
				return `Client ${this.dataService.item.dynamicProperties['person']['firstName'] || ''} ${this.dataService.item.dynamicProperties['person']['lastName'] || ''}`;
			} else {
				return `Client ${this.dataService.item.dynamicProperties['company']['signature'] || ''}`;
			}
		}

		return '';
	}

	@autobind
	private handleModeChange(mode: ControlMode): void {
		this.setState({ mode: mode });
	}

	addContact = (): void => {
		if (this.dataService.item != null) {
			this.dataService.item.dynamicProperties['contacts'].push({});
		}
	};

	@autobind
	private async handleSave(): Promise<void> {
		const item = this.dataService.item;
		const errors = await item.validate(true);
		if (errors.length == 0) {
			this.props.setBusy(true);
			if (item.type == 0) {
				item.displayName = `${item.dynamicProperties.person.firstName} ${item.dynamicProperties.person.lastName}`;
			} else {
				item.displayName = `${item.dynamicProperties.company.signature}`;
			}

			this.dataService.save()
				.then(() => {
					this.props.setBusy(false);
					this.handleModeChange('display');
					this.toast.showDataSaved();
					this.props.history.replace(`/crm/clients`);
				})
				.catch((error) => {
					this.props.setBusy(false);
					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
				});
		} else {
			const err = errors.map(e => e.text);
			this.toast.showToast('Validation error!', err, 'error');
		}
	}

	@autobind
	private handleCancel(): void {
		this.props.history.push('/crm/clients');
	}

	render(): React.ReactNode {
		if (this.dataService == null || this.dataService.item == null || this.state.schema == null) {
			return null;
		}

		if (this.dataService.item.dynamicProperties['notes'] == null) {
			this.dataService.item.dynamicProperties['notes'] = [];
		}

		const isNew = this.props.match.params.id == 'Person' || this.props.match.params.id == 'Company';

		return (
			<ClientFormView
				identityService={this.identityService}
				title={this.title}
				mode={this.state.mode}
				onModeChange={this.handleModeChange}
				cancel={this.handleCancel}
				save={this.handleSave}
				model={this.dataService.item}
				quoteDataSet={this.quoteListDataService}
				schema={this.state.schema}
				contextService={this.contextService}
				isNew={isNew}
				setBusy={this.props.setBusy}
				history={this.props.history}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/crm/clients/form/client-form.controller.tsx